import {Injectable, OnDestroy} from '@angular/core';
import {environment} from '../../environments/environment';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import * as jose from 'jose';
import {ApiMode, ConfigService, CspType, ResponseWrapper, SDKMode, VaultAuthFinishResponse} from '@shared-library';
import {NGXLogger} from 'ngx-logger';
import {SentryContextService} from './sentry-context.service';

// New backends use HttpOnly cookies; these helpers support older backends.
export const FASTEN_AUTH_VAULT_COOKIE_NAME = 'fasten_connect_auth_vault'
export const VAULT_AUTH_SESSION_RECHECK_DELAY_MS = 100

export enum CookieProbeScope {
  All = 'all',
  Regular = 'regular',
}

export enum VaultAuthHandoffPurpose {
  IdentityVerification = 'identity_verification_handoff',
  BridgeConnect = 'bridge_connect_handoff',
}

export interface VaultAuthSessionDebugInfo {
  cookieName: string
  isSet: boolean
}

interface CookieSupportResponse {
  supported: boolean
}

interface CookieProbeResponse {
  probe: string
}

export interface VaultAuthCodeExpiry {
  expires: number
}

export type VaultAuthBeginResult = Partial<VaultAuthCodeExpiry & VaultAuthFinishResponse>

// Session claims returned by HttpOnly backends or decoded from legacy cookies.
export interface VaultSessionResponse {
  vault_profile_id: string
  email: string
  api_mode: ApiMode
  has_verified_identity: boolean
  verified_identity_csp_type?: CspType
  session_issued_at: number
  session_expires_at: number
}

export interface VaultAuthHandoffResponse {
  handoff_token: string
  expires_at: number
}

const SESSION_REFRESH_WINDOW_MS = 60_000
export const SESSION_CACHE_TTL_MS = 30_000
const AUTH_STATE_STORAGE_KEY = 'fasten_connect_vault_auth_event'

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {

  public IsAuthenticatedSubject = new BehaviorSubject<boolean>(false)
  private cookieSupported?: boolean

  private session?: VaultSessionResponse
  private sessionCachedAt?: number
  private sessionRequest?: Promise<VaultSessionResponse | null>
  private sessionGeneration = 0

  private readonly storageListener = (event: StorageEvent): void => {
    if (event.key !== AUTH_STATE_STORAGE_KEY || !event.newValue) {
      return
    }
    try {
      if (JSON.parse(event.newValue)?.type === 'signout') {
        this.publishAuthenticationState(false)
        this.ClearSession()
      }
    } catch {
      // Ignore unrelated or malformed storage values.
    }
  }

  constructor(
    private _httpClient: HttpClient,
    private configService: ConfigService,
    private sentryContext: SentryContextService,
    private logger: NGXLogger,
  ) {
    window.addEventListener('storage', this.storageListener)
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.storageListener)
  }

  /** Attach the tenant public ID to vault auth requests. */
  private requestOptions(extraParams?: Record<string, string>): { withCredentials: true, params: Record<string, string> } {
    const params = {public_id: this.configService.systemConfig$.publicId, ...extraParams}

    return {
      withCredentials: true,
      params,
    }
  }
  public async VaultAuthBegin(email: string, cspPromptForce?: boolean): Promise<VaultAuthBeginResult | undefined> {
    const response = await firstValueFrom(this._httpClient.post<ResponseWrapper<VaultAuthCodeExpiry> | VaultAuthFinishResponse>(
      `${environment.connect_api_endpoint_base}/bridge/vault_auth_begin`,
      {email, csp_prompt_force: cspPromptForce},
      this.requestOptions(),
    ))
    this.ClearSession()
    return 'data' in response ? response.data : response
  }

  public async VaultAuthFinish(email: string, code: string): Promise<any> {
    const response = await firstValueFrom(this._httpClient.post<VaultAuthFinishResponse>(
      `${environment.connect_api_endpoint_base}/bridge/vault_auth_finish`,
      {email, code},
      this.requestOptions(),
    ))
    this.ClearSession()
    return response
  }

  public async VaultAuthResendCode(email: string): Promise<VaultAuthCodeExpiry | undefined> {
    const response = await firstValueFrom(this._httpClient.post<ResponseWrapper<VaultAuthCodeExpiry>>(
      `${environment.connect_api_endpoint_base}/bridge/vault_auth_resend_code`,
      {email},
      this.requestOptions(),
    ))
    return response.data
  }

  /** Clear a legacy or server-managed auth cookie. */
  public async Signout(): Promise<unknown> {
    this.publishAuthenticationState(false)
    this.ClearSession()
    this.notifyOtherTabsOfSignout()

    if (!this.usesHttpOnlyCookie) {
      deleteCookie(FASTEN_AUTH_VAULT_COOKIE_NAME)
      return undefined
    }

    try {
      return await firstValueFrom(this._httpClient.post(
        `${environment.connect_api_endpoint_base}/bridge/vault_auth_signout`,
        null,
        this.requestOptions(),
      ))
    } catch (error) {
      this.logger.error("error signing out", error)
      return undefined
    } finally {
      // Also clears a readable cookie during backend migration.
      deleteCookie(FASTEN_AUTH_VAULT_COOKIE_NAME)
    }
  }

  /** Load the cached verified session when possible. */
  /** Pass true to force a refresh; concurrent callers share the request. */
  public GetSession(forceRefresh = false): Promise<VaultSessionResponse | null> {
    const cachedSession = this.session
    if (!forceRefresh && cachedSession && this.isCachedSessionUsable()) {
      return Promise.resolve(cachedSession)
    }

    if (this.sessionRequest) {
      return this.sessionRequest
    }

    const requestGeneration = this.sessionGeneration
    const sessionLoader = this.usesHttpOnlyCookie
      ? this.fetchHttpOnlySession()
      : this.getLegacyCookieSession()
    const request = sessionLoader
      .then((sessionResponse) => {
        if (!sessionResponse) {
          this.publishAuthenticationState(false)
          return null
        }
        if (sessionResponse.api_mode !== this.configService.systemConfig$.apiMode) {
          this.logger.warn('vault auth session API mode does not match the configured API mode')
          this.ClearSession()
          this.publishAuthenticationState(false)
          return null
        }
        // Do not restore a session cleared while this request was in flight.
        if (requestGeneration === this.sessionGeneration) {
          this.storeSession(sessionResponse)
        }
        return sessionResponse
      })
      .catch((error: unknown) => {
        this.ClearSession()
        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
          // An anonymous session check normally returns 401 or 403.
          this.publishAuthenticationState(false)
          return null
        }
        this.logger.error("error fetching session", error)
        throw error
      })

    this.sessionRequest = request
    const clearRequest = () => {
      if (this.sessionRequest === request) {
        this.sessionRequest = undefined
      }
    }
    void request.then(clearRequest, clearRequest)
    return request
  }

  /** Drop cached session state after the cookie changes. */
  public ClearSession(): void {
    this.sessionGeneration++
    this.session = undefined
    this.sessionCachedAt = undefined
    this.sessionRequest = undefined
  }

  public async IsVaultAuthSessionActive(): Promise<boolean> {
    const isActive = (await this.GetSession(true)) !== null
    if (!isActive) {
      this.logger.warn('Vault auth session is not active')
    }
    return isActive
  }

  public async WaitForVaultAuthSession(): Promise<boolean> {
    if (await this.IsVaultAuthSessionActive()) {
      this.sentryContext.recordAuthCookieResult('present', 'immediate')
      return true
    }

    await new Promise<void>((resolve) => setTimeout(resolve, VAULT_AUTH_SESSION_RECHECK_DELAY_MS))
    const isActive = await this.IsVaultAuthSessionActive()
    this.sentryContext.recordAuthCookieResult(isActive ? 'present' : 'missing', 'after_retry')
    return isActive
  }

  public async CheckCookieSupport(scope: CookieProbeScope = CookieProbeScope.All): Promise<boolean> {
    const fallbackAvailable = scope === CookieProbeScope.All && this.CanUseStorageAccessFallback()
    try {
      const scopeParams: Record<string, string> = {}
      if (scope === CookieProbeScope.Regular) {
        scopeParams['regular_only'] = 'true'
      }
      const expectedProbe = await this.setCookieProbe(scopeParams)
      const response = await firstValueFrom(this._httpClient.post<ResponseWrapper<CookieSupportResponse>>(
        `${environment.connect_api_endpoint_base}/bridge/cookie_support`, null, {
          withCredentials: true,
          params: {probe: expectedProbe, ...scopeParams},
        }
      ))
      const supported = response?.data?.supported

      if (typeof supported !== 'boolean') {
        throw new Error(`Invalid cookie support response: ${supported}`)
      }

      this.cookieSupported = supported
      this.sentryContext.recordCookieProbeResult(
        scope,
        supported ? 'supported' : 'blocked',
        fallbackAvailable,
      )
      return supported
    } catch (error) {
      this.sentryContext.recordCookieProbeResult(scope, 'error', fallbackAvailable)
      throw error
    }
  }

  private async setCookieProbe(params: Record<string, string>): Promise<string> {
    const response = await firstValueFrom(this._httpClient.post<ResponseWrapper<CookieProbeResponse>>(
      `${environment.connect_api_endpoint_base}/bridge/cookie_probe`, null, {
        withCredentials: true,
        params,
      }
    ))
    const probe = response?.data?.probe

    if (typeof probe !== 'string' || probe.length === 0) {
      throw new Error('Invalid cookie probe response')
    }

    return probe
  }

  public RequiresStorageAccessFallback(): boolean {
    const cookieIsBlocked = this.cookieSupported === false
    return cookieIsBlocked && this.CanUseStorageAccessFallback()
  }

  public CanUseStorageAccessFallback(): boolean {
    const usesBrowserSdk = this.configService.systemConfig$.sdkMode === SDKMode.None
    const storageAccessApiAvailable = typeof document.hasStorageAccess === 'function'
      && typeof document.requestStorageAccess === 'function'

    return usesBrowserSdk && storageAccessApiAvailable
  }

  /** Summarize the auth session without exposing its value. */
  public async GetVaultAuthSessionDebugInfo(): Promise<VaultAuthSessionDebugInfo> {
    const isSet = await this.IsVaultAuthSessionActive()
    return {
      cookieName: FASTEN_AUTH_VAULT_COOKIE_NAME,
      isSet,
    }
  }

  /** Get a legacy session JWT or a handoff token scoped to the required purpose. */
  public async GetVaultAuthHandoffToken(purpose: VaultAuthHandoffPurpose): Promise<string> {
    if (!Object.values(VaultAuthHandoffPurpose).includes(purpose)) {
      throw new Error(`Unsupported vault auth handoff purpose: ${purpose}`)
    }
    if (this.usesHttpOnlyCookie) {
      const response = await firstValueFrom(this._httpClient.get<ResponseWrapper<VaultAuthHandoffResponse>>(
        `${environment.connect_api_endpoint_base}/bridge/vault_auth_handoff`, this.requestOptions({purpose})
      ))
      const handoffToken = response?.data?.handoff_token
      if (!handoffToken) {
        this.logger.error('invalid vault auth handoff response', {purpose, response})
        throw new Error('Invalid vault auth handoff response')
      }
      return handoffToken
    }

    const legacyToken = getCookie(FASTEN_AUTH_VAULT_COOKIE_NAME)
    if (!legacyToken || !await this.getLegacyCookieSession()) {
      throw new Error('Legacy vault auth cookie is not available or is invalid')
    }
    // Legacy backends accept the readable session JWT as the handoff token.
    return legacyToken
  }

  public async IsAuthenticated(): Promise<boolean> {
    const session = await this.GetSession()
    const isAuthenticated = session !== null
    this.publishAuthenticationState(isAuthenticated)
    return isAuthenticated
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////
  //Private Methods
  /////////////////////////////////////////////////////////////////////////////////////////////////

  private isCachedSessionUsable(): boolean {
    if (!this.session || this.sessionCachedAt === undefined) {
      return false
    }

    const now = Date.now()
    const cacheIsFresh = now - this.sessionCachedAt < SESSION_CACHE_TTL_MS
    const cookieIsAvailable = this.usesHttpOnlyCookie || Boolean(getCookie(FASTEN_AUTH_VAULT_COOKIE_NAME))
    const sessionIsFresh = this.session.session_expires_at * 1000 > now + SESSION_REFRESH_WINDOW_MS

    return cacheIsFresh && cookieIsAvailable && sessionIsFresh
  }

  private storeSession(session: VaultSessionResponse): void {
    this.session = session
    this.sessionCachedAt = Date.now()
    this.publishAuthenticationState(true)
  }

  private async fetchHttpOnlySession(): Promise<VaultSessionResponse> {
    const response = await firstValueFrom(this._httpClient.get<ResponseWrapper<VaultSessionResponse>>(
      `${environment.connect_api_endpoint_base}/bridge/vault_auth_session`, this.requestOptions(),
    ))
    return response.data
  }

  private get usesHttpOnlyCookie(): boolean {
    const organization = this.configService.systemConfig$.org
    return organization?.vault_auth_cookie_httponly === true
  }

  private notifyOtherTabsOfSignout(): void {
    try {
      localStorage.setItem(AUTH_STATE_STORAGE_KEY, JSON.stringify({
        type: 'signout',
        at: Date.now(),
        nonce: Math.random().toString(36).slice(2),
      }))
    } catch {
      // Embedded contexts may deny localStorage.
    }
  }

  private publishAuthenticationState(authenticated: boolean){
    if(this.IsAuthenticatedSubject.value != authenticated){
      this.IsAuthenticatedSubject.next(authenticated)
    }
  }

  private async getLegacyCookieSession(): Promise<VaultSessionResponse | null> {
    const authToken = getCookie(FASTEN_AUTH_VAULT_COOKIE_NAME)
    if (!authToken) {
      this.logger.debug('legacy vault auth cookie is not readable', {
        cookieName: FASTEN_AUTH_VAULT_COOKIE_NAME,
        browserTime: new Date().toISOString(),
      })
      return null
    }

    const verificationStartedAt = new Date()
    try {
      const jwks = jose.createRemoteJWKSet(new URL(environment.jwks_uri))
      const {payload} = await jose.jwtVerify(authToken, jwks, {
        issuer: environment.connect_api_jwt_issuer_host,
        audience: environment.connect_api_jwt_issuer_host,
      })
      const configuredOrgID = this.configService.systemConfig$.org?.id
      const audiences = Array.isArray(payload.aud) ? payload.aud : (payload.aud ? [payload.aud] : [])
      if (configuredOrgID && !audiences.includes(configuredOrgID)) {
        this.logger.warn('legacy vault auth JWT is not valid for the configured organization')
        return null
      }
      return this.legacyPayloadToSession(payload)
    } catch (error) {
      const browserTime = new Date()
      const browserNowSeconds = Math.floor(browserTime.getTime() / 1000)
      const claimError = error instanceof jose.errors.JWTClaimValidationFailed || error instanceof jose.errors.JWTExpired
        ? error : undefined
      // JOSE errors can contain the entire payload. Only log allowlisted metadata;
      // these timestamps are diagnostic data, not an authenticated session.
      const numericClaim = (name: 'iat' | 'nbf' | 'exp'): number | null => {
        const value = claimError?.payload[name]
        return typeof value === 'number' && Number.isFinite(value) ? value : null
      }
      const issuedAt = numericClaim('iat')
      const notBefore = numericClaim('nbf')
      const expiresAt = numericClaim('exp')
      this.logger.error('failed to verify legacy vault auth JWT', {
        errorName: error instanceof Error ? error.name : 'UnknownError',
        errorCode: error instanceof jose.errors.JOSEError ? error.code : undefined,
        failedClaim: claimError?.claim,
        claimFailureReason: claimError?.reason,
        cookieName: FASTEN_AUTH_VAULT_COOKIE_NAME,
        cookieReadable: true,
        httpOnlyMode: this.usesHttpOnlyCookie,
        verificationStartedAt: verificationStartedAt.toISOString(),
        browserTime: browserTime.toISOString(),
        browserNowSeconds,
        clockToleranceSeconds: 0,
        issuedAt,
        notBefore,
        expiresAt,
        // Positive values indicate timestamps ahead of the browser's clock.
        issuedAtMinusBrowserNowSeconds: issuedAt === null ? null : issuedAt - browserNowSeconds,
        notBeforeMinusBrowserNowSeconds: notBefore === null ? null : notBefore - browserNowSeconds,
        expiresAtMinusBrowserNowSeconds: expiresAt === null ? null : expiresAt - browserNowSeconds,
        notBeforeBackdateSeconds: issuedAt === null || notBefore === null ? null : issuedAt - notBefore,
      })
      return null
    }
  }

  private legacyPayloadToSession(payload: jose.JWTPayload): VaultSessionResponse | null {
    const expiresAt = Number(payload.exp)
    if (!payload.sub || !Number.isFinite(expiresAt)) {
      this.logger.error('legacy vault auth JWT is missing its subject or expiration')
      return null
    }
    if (payload['api_mode'] !== this.configService.systemConfig$.apiMode) {
      this.logger.warn('legacy vault auth JWT API mode does not match the configured API mode')
      return null
    }

    return {
      vault_profile_id: payload.sub,
      email: String(payload['email'] ?? ''),
      api_mode: payload['api_mode'] as ApiMode,
      has_verified_identity: Boolean(payload['has_verified_identity']),
      verified_identity_csp_type: payload['verified_identity_csp_type'] as CspType | undefined,
      session_issued_at: Number(payload.iat ?? 0),
      session_expires_at: expiresAt,
    }
  }

}

// Compatibility helpers for backends that expose the auth cookie.
export function getCookie(name: string): string {
  const cookieName = `${name}=`
  for (const cookie of decodeURIComponent(document.cookie).split(';')) {
    const normalizedCookie = cookie.replace(/^\s+/g, '')
    if (normalizedCookie.startsWith(cookieName)) {
      return normalizedCookie.substring(cookieName.length)
    }
  }
  return ''
}

export function setCookie(
  name: string,
  value: string,
  expireDays: number,
  path: string = '',
  domain: string = '',
  partitioned: boolean = false,
): void {
  const expiresAt = new Date()
  expiresAt.setTime(expiresAt.getTime() + expireDays * 24 * 60 * 60 * 1000)
  const cookiePath = path ? `; path=${path}` : ''
  const cookieDomain = domain ? `; domain=${domain}` : ''
  const cookieSecurity = partitioned ? '; SameSite=None; Secure; Partitioned' : '; SameSite=Lax'
  document.cookie = `${name}=${value}; expires=${expiresAt.toUTCString()}${cookiePath}${cookieDomain}${cookieSecurity}`
}

export function deleteCookie(name: string): void {
  setCookie(name, '', -99999)
  setCookie(name, '', -99999, '/')
  setCookie(name, '', -99999, '/', environment.connect_base_domain)
  // Partitioned and regular cookies occupy different storage slots.
  setCookie(name, '', -99999, '/', environment.connect_base_domain, true)
}
